import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { dayJS } from "../../utils/datetime";
import { useNotification } from "../../context/notification";

import { useHistory, useParams, useRouteMatch } from "react-router";
import ClassNames from "classnames";
import { apiRequest } from "../../utils/api";
import styles from "./LogsArchiveSession.module.scss";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import Stepper from "../../components/Stepper";
import NotesSidebar from "../../components/NotesSidebar";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import { Framework, SessionStatus } from "../../config/Config";
import Accordion from "../../components/Accordion";
import SessionSummary from "../../components/SessionSummary";
import SubjectListWithTarget from "../../components/SubjectListWithTarget";
import ArchiveMessageCard from "../../components/ArchiveMessageCard";
import SatisfactionRating from "../../components/SatisfactionRating";
import ReviewDateSummary from "../../components/ReviewDateSummary";
import CheckPointSummary from "../../components/CheckPointSummary";
import { getLocalValue, getUID } from "../../utils/auth";

import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { getQueryVariable } from "../../utils/getQueryParam";
import Select from "../../components/Select";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import { useTranslation } from "react-i18next";
import { IsCampaignManager, IsSessionReadOnly } from "../../config/Roles";
import { logsPath } from "../../config/GetPagePath";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import SessionDeleteButton from "../../components/SessionDeleteButton";

export const LogsArchiveSession = () => {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const { id } = useParams();
  const matchLogRoute = useRouteMatch("/logs/archive-session/:id");
  const urlParamDate = getQueryVariable("date");
  const subjectId = getQueryVariable("subjectId");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [checkpoints, setCheckpoints] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [reportingManager, setReportingManager] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [coachRating, setCoachRating] = useState(0);
  const [coacheeRating, setCoacheeRating] = useState(null);
  const [finalData, setFinalData] = useState([]);
  const [sessionSummaryText, setSessionSummaryText] = useState(null);
  const [ratingValue, setRatingValue] = useState(null);
  const [isPDFlow, SetIsPDFlow] = useState(true);
  const [clockRunCheck, setClockRunCheck] = useState(false);
  const [notesAccessible, setNotesAccessible] = useState(false);
  const [dateDropdownList, setDateDropdownList] = useState(null);
  const [sessionTotalDuration, setSessionTotalDuration] = useState({
    totalDuration: 0,
    plan: 0,
    doPhase: 0,
    check: 0,
    act: 0,
  });

  const intialForm = {
    coacheeRating: null,
    ratingValue: 0,
  };
  if (!localStorage.sessionArchieveData) {
    localStorage.setItem("sessionArchieveData", JSON.stringify(intialForm));
  }

  useEffect(() => {
    // get session details
    apiRequest
      .get(`/api/getSessionDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        setSessionDetails(data);
        if (data.subject) setSubjectList(data.subject);
      })
      .catch(error => {
        console.error(error);
      });

    // get checkpoint details
    apiRequest
      .get(`/api/getCheckpointDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        if (data && data.progress_data) {
          setCheckpoints(data.progress_data);
        }
        if (data && data.final_data) {
          setFinalData(data.final_data);
        }
        if (data && data.summary_data) {
          setSessionSummaryText(data.summary_data.summary);
        }
      })
      .catch(error => {
        console.error(error);
      });

    // get duration details
    apiRequest
      .get(`/api/getSessionDurationInfo?session_id=${id}`)
      .then(response => {
        const data = response.data.data;

        if (data.length > 0) {
          let totalDuration = 0,
            plan = 0,
            doPhase = 0,
            check = 0,
            act = 0;
          [...data].map(phase => {
            totalDuration = totalDuration + phase.duration;
            plan =
              phase.session_phase_name === "Plan"
                ? plan + phase.duration
                : plan;
            doPhase =
              phase.session_phase_name === "Do"
                ? doPhase + phase.duration
                : doPhase;
            check =
              phase.session_phase_name === "Check"
                ? check + phase.duration
                : check;
            act =
              phase.session_phase_name === "Act" ? act + phase.duration : act;
          });

          setSessionTotalDuration({
            ...sessionTotalDuration,
            totalDuration,
            plan,
            doPhase,
            check,
            act,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });

    // get reporting manager
    GetReportingManagerForSession(id).then(data => {
      if (data) {
        setReportingManager(data);
      }
    });
  }, []);

  useEffect(() => {
    // get session date dropdown for HR view
    if (urlParamDate) {
      apiRequest
        .get(
          `/api/getHRViewWarningDates?session_id=${id}&subject_id=${subjectId}`
        )
        .then(response => {
          let data = response.data.data;
          data = data.dates.map(item => {
            return {
              value: item,
              label: item,
            };
          });
          setDateDropdownList(data);
        })
        .catch(error => {
          console.error(error);
        });
    }

    setTimeout(() => {
      if (localStorage.sessionArchieveData) {
        const tmp = JSON.parse(localStorage.sessionArchieveData);
        if (tmp.coacheeRating && tmp.coacheeRating !== null) {
          setCoacheeRating(tmp.coacheeRating);
        }
        if (tmp.ratingValue) {
          setRatingValue(parseInt(tmp.ratingValue));
        }
      }
    }, 2000);
  }, []);

  useEffect(() => {
    const frameworkValue = getSessionValue("framework");
    // Check if sessionDetails is populated
    if (!_.isEmpty(sessionDetails) && sessionDetails.session) {
      // Explicitly log comparison result
      const isPDFlowValue = frameworkValue === Framework.PD;
      SetIsPDFlow(isPDFlowValue);
      const reviewDetails = Array.isArray(sessionDetails.review_details)
        ? sessionDetails.review_details[0]
        : sessionDetails.review_details;
      if (_.some(reviewDetails, _.isEmpty)) {
        setCoachRating(reviewDetails.coachee_behaviour_rating);
        setCoacheeRating(reviewDetails.coachee_rating);
        setNotesAccessible(sessionDetails.session.status == 6);
        setTimeout(() => {
          setClockRunCheck(true);
        }, 500);
      }
    }
  }, [sessionDetails]);

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionDetails) || !sessionDetails.session) return true;
    return IsSessionReadOnly(
      sessionDetails.session.coach_id,
      sessionDetails.session.coachee_id
    );
  }, [sessionDetails]);

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  const submitFeedback = () => {
    if (!ratingValue) {
      addNotification(t("archiveSession.selectYourFeedback"), "error");
      return;
    }

    setIsLoading(true);
    const formData = {
      session_id: parseInt(id),
      coachee_satisfaction: ratingValue,
      created_by: sessionDetails.session.coach_id,
    };
    apiRequest
      .post(`/api/archiveSession`, formData)
      .then(resp => {
        if (resp.status === 200) {
          addNotification(`Thank you for the feedback`, "success");

          setTimeout(() => {
            history.push(logsPath());
          }, 2000);
          setCoacheeRating(ratingValue.toString());
          localStorage.removeItem("sessionArchieveData");
        } else {
          setIsLoading(false);
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
        setIsLoading(false);
      })
      .finally(() => {});
  };

  const getDurationSplit = timestamp => {
    const hours = Math.floor(timestamp / 60 / 60);
    const minutes = Math.floor(timestamp / 60) - hours * 60;
    const seconds = timestamp % 60;
    return `
      ${hours === 0 ? "" : hours < 10 ? "0" + hours + ":" : hours + " hours "}
      ${minutes === 0 ? "" : minutes + " minutes "}
      ${seconds === 0 ? "" : seconds + " seconds "}
      `;
  };

  const handleDateChange = value => {
    if (value === dateDropdownList[0].value) {
      history.push(`/warningLetters/session/${id}:${subjectId}`);
    } else {
      history.push(
        `/logs/archive-session/${getSessionValue("session_id")}?date=${dayJS(
          value
        ).format("DD MMM YYYY")}&subjectId=${subjectId}`
      );
    }
  };

  const getSessionLabel = () => {
    if (urlParamDate) return urlParamDate;

    return sessionDetails?.session?.status === SessionStatus.INCOMPLETE
      ? t("archiveSession.incompleteSession")
      : t("archiveSession.archiveSession");
  };

  return (
    <>
      {!_.isEmpty(sessionDetails) && (
        <DashboardLayout
          appBarContent={
            clockRunCheck && (
              <>
                {!urlParamDate && (
                  <Stepper
                    phase={"Done"}
                    phaseLabel={"Done"}
                    sessionId={id}
                    clockRun={false}
                    getTotalTimeSpent={getTotalTime}
                    isPDFlow={isPDFlow}
                  />
                )}
                {urlParamDate && (
                  <div className={styles.appBar}>
                    <BackButton style={{ paddingBottom: "0" }} />
                    {dateDropdownList && (
                      <Select
                        className={styles.topDropdown}
                        list={dateDropdownList}
                        value={dayJS(urlParamDate).format("YYYY-MM-DD")}
                        onChange={e => {
                          handleDateChange(e.target.value);
                        }}
                      />
                    )}
                  </div>
                )}
              </>
            )
          }
          rightSidebarContent={
            <NotesSidebar
              sessionID={id}
              isCoach={sessionDetails["coach_id"] === parseInt(getUID())}
              accessible={notesAccessible && !isReadOnly}
            />
          }
          contentClass={styles.dashboardLayout__content}
        >
          {sessionDetails && (
            <div className={styles.pageWrapper}>
              <PageContent indent>
                <Section>
                  <div className={styles.detailsContainer}>
                    {!urlParamDate && (
                      <BackButton
                        text={
                          matchLogRoute
                            ? t("archiveSession.backToLog")
                            : t("archiveSession.backtoSession")
                        }
                      />
                    )}
                    <SectionTitle
                      append={
                        <>
                          {getLocalValue("employee_id") === reportingManager &&
                            IsCampaignManager() &&
                            sessionDetails?.session?.status ===
                              SessionStatus.INCOMPLETE && (
                              <SessionDeleteButton sessionId={id} isLogsRoute />
                            )}
                        </>
                      }
                    >
                      {getSessionLabel()}
                    </SectionTitle>
                    {!isPDFlow && (
                      <>
                        {sessionDetails?.session?.status !==
                          SessionStatus.INCOMPLETE &&
                          !urlParamDate && (
                            <ArchiveMessageCard
                              header={t("archiveSession.areYouSatisfied")}
                              rating={
                                <SatisfactionRating
                                  ratingValue={ratingValue}
                                  coacheeRating={coacheeRating}
                                  isReadOnly={isReadOnly}
                                  onChange={val => {
                                    if (localStorage.sessionArchieveData) {
                                      const tmp = JSON.parse(
                                        localStorage.sessionArchieveData
                                      );
                                      tmp.coacheeRating = coacheeRating;
                                      tmp.ratingValue = val;
                                      localStorage.setItem(
                                        "sessionArchieveData",
                                        JSON.stringify(tmp)
                                      );
                                    }
                                    setRatingValue(val);
                                  }}
                                />
                              }
                              description={t(
                                "archiveSession.pleaseReadTHeSessionSummary"
                              )}
                              footer={
                                coacheeRating === null ||
                                coacheeRating === undefined ? (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={submitFeedback}
                                    disabled={isReadOnly}
                                  >
                                    {t("archiveSession.areYouSatisfied")}
                                  </Button>
                                ) : (
                                  ""
                                )
                              }
                            />
                          )}
                      </>
                    )}
                    <div className={styles.sessionDetailsBlock}>
                      <SessionBasicInfo sessionData={sessionDetails.session} />
                      <div className={styles.row}>
                        <div className={styles.rowLeft}>
                          {t("archiveSession.totalDuration")}
                        </div>
                        <div className={styles.rowRight}>
                          {getDurationSplit(sessionTotalDuration.totalDuration)}
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.rowLeft}>
                          {t("archiveSession.durationBreakdown")}
                        </div>
                        <div className={styles.rowRight}>
                          <ul>
                            <li>
                              P - {getDurationSplit(sessionTotalDuration.plan)}
                            </li>
                            <li>
                              D -{" "}
                              {getDurationSplit(sessionTotalDuration.doPhase)}
                            </li>
                            {!isPDFlow && (
                              <>
                                <li>
                                  C -{" "}
                                  {getDurationSplit(sessionTotalDuration.check)}
                                </li>
                              </>
                            )}
                            <li>
                              A - {getDurationSplit(sessionTotalDuration.act)}
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div>
                        <Accordion
                          summary={t("sessionSummary.viewSessionSummary")}
                          details={<SessionSummary id={id}></SessionSummary>}
                        />
                        <Accordion
                          summary={t("archiveSession.viewReviewSessionSummary")}
                          details={
                            <div className={styles.detailsContainer}>
                              <div className={styles.sessionRatingBlock}>
                                <div className={styles.ratingCard}>
                                  <div className={styles.label}>
                                    {t("archiveSession.coacheesBehaviour")}
                                  </div>
                                  <SatisfactionRating
                                    ratingValue={coachRating}
                                    coacheeRating={coachRating}
                                    onChange={val => {}}
                                  />
                                  {/* <div className={styles.rowRight}>
                                    <StarRatingCard
                                      value={coachRating}
                                      readOnly
                                    />
                                  </div> */}
                                </div>
                              </div>
                            </div>
                          }
                        />

                        {!isPDFlow && <ReviewDateSummary id={id} />}
                      </div>
                    </div>
                    {!isPDFlow && (
                      <>
                        <div
                          className={styles.sessionUpdateCheckpointScoresBlock}
                        >
                          <CheckPointSummary id={id} />
                        </div>
                        <div className={styles.sessionFinalReviewBlock}>
                          <div
                            className={ClassNames(
                              styles.sessionGrowFormBlock,
                              styles.borderTop
                            )}
                          >
                            <div className={styles.row}>
                              <div className={styles.rowLeft}>
                                <div className={styles.labelTitle}>
                                  {t("archiveSession.finalReview")}
                                </div>
                              </div>
                              <div className={styles.rowRight}>
                                <div className={styles.content}>
                                  <div className={styles.dateTime}>
                                    <div className={styles.date}>
                                      <CalendarTodayIcon
                                        className={styles.icon}
                                      />{" "}
                                      <span>
                                        {dayJS(
                                          getSessionValue("review_datetime")
                                        ).format("D MMM YYYY")}
                                      </span>
                                    </div>
                                    <div className={styles.time}>
                                      <ScheduleIcon className={styles.icon} />{" "}
                                      <span>
                                        {dayJS(
                                          getSessionValue("review_datetime")
                                        ).format("H:mm A")}
                                      </span>
                                    </div>
                                  </div>
                                  {subjectList && subjectList.length > 0 && (
                                    <SubjectListWithTarget
                                      list={
                                        subjectList && subjectList.length > 0
                                          ? subjectList
                                          : []
                                      }
                                      finalData={
                                        finalData && finalData.length > 0
                                          ? finalData
                                          : null
                                      }
                                      setFinalData={setFinalData}
                                      isReadOnly={isReadOnly}
                                      hideCurrent
                                      getOverwriting
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                            {sessionSummaryText && (
                              <div className={styles.row}>
                                <div className={styles.rowLeft}>
                                  <div className={styles.labelTitle}>
                                    {t("archiveSession.summary")}
                                  </div>
                                </div>
                                <div className={styles.rowRight}>
                                  {sessionSummaryText}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Section>
              </PageContent>
            </div>
          )}
        </DashboardLayout>
      )}
    </>
  );
};
